import { useCallback, useEffect, useState } from 'react';
import ReactLoading from 'react-loading';
import { useSearchParams } from 'react-router-dom';
import EmptyPage from '../../../empty-page/emptyPage';
import { standardDate } from '../../../../utils/formatDate';
import orgBillingData, { billingCount } from '../../../../pages/orgBilling/orgBillingFun/fetchOrgBilling';
import Pagination from '../../../pagination/Pagination';
import { fetchActBillingCount, fetchActBillingsData } from './actBillingCountFn/fetchActBillingCountFn';

const ActivityBillings = () => {
    const [searchParams, setSearchParams] = useSearchParams();
    const selectedStatus = searchParams.get('status') || 'completed';
    const selectedApproval = searchParams.get('approval') || 'approved';
    const pageNumber = Number(searchParams.get('page')) || 1;
    const [loading, setLoading] = useState(false);
    const [billingData, setBillingData] = useState([]);

    const tableHeader = [
        'Package Name',
        'Activity Date',
        'Booked Slots',
        'Payout Date',
        'Total Amount',
        'Commission',
        'Amount Paid',
        'Action'
    ];

    const limit = 10;
    const offSet = (pageNumber - 1) * limit;
    // const [limit, setLimit] = useState(10);
    const [totalCount, setTotalCount] = useState(0);
    const totalData = totalCount;
    const totalPages = Math.ceil(totalData / limit);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        });
    }, []);

    const getBookedSlots = (row) => {
        // Old/direct field
        if (typeof row?.bookedSlots === 'number') {
            return row.bookedSlots;
        }

        // New slot-based structure
        if (Array.isArray(row?.slots)) {
            return row.slots.reduce((total, slot) => total + (Number(slot?.bookedSlots) || 0), 0);
        }

        return 0;
    };

    const updateURL = useCallback(
        (params) => {
            const current = Object.fromEntries([...searchParams]);

            Object.keys(params).forEach((key) => {
                if (params[key] === '' || params[key] === null) {
                    delete current[key];
                } else {
                    current[key] = params[key];
                }
            });
            setSearchParams(current);
        },
        [searchParams, setSearchParams]
    );

    useEffect(() => {
        if (!totalPages) return;

        if (pageNumber > totalPages) {
            updateURL({ page: totalPages });
        }
        //eslint-disable-next-line
    }, [totalPages, pageNumber, updateURL]);

    const paginationClick = (number, offSetNumber) => {
        if (Math.ceil(totalData / limit) > 1) {
            updateURL({ page: number });
            window.scrollTo({
                top: 0,
                behavior: 'smooth'
            });
        }
    };
    useEffect(() => {
        if (!selectedStatus) return;
        fetchActBillingCount(setTotalCount, selectedStatus, selectedApproval, setLoading);
    }, [selectedStatus, selectedApproval]);

    useEffect(() => {
        if (!selectedApproval || !selectedStatus) return;
        fetchActBillingsData(selectedStatus, selectedApproval, limit, offSet, setBillingData, setLoading);
    }, [selectedStatus, selectedApproval, limit, offSet]);

    return (
        <>
            {loading && (
                <div className='loadingBackground'>
                    <ReactLoading type='spin' color='#006aff' className='loadingSpin' />
                </div>
            )}
            <div className='billingContainer table-top-container'>
                <div className='alignCenter filter bookingSticky'>
                    {/* <input
                        placeholder='Search...'
                        name='orgSearch'
                        value={searchText}
                        className='orgBillSearch'
                        onChange={(sea) => handleTextChange(sea, setSearchText)}
                    /> */}
                    <div className='orgBookingFilters'>
                        <select
                            name='type'
                            id='type'
                            className='select'
                            value={selectedStatus}
                            onChange={(e) => {
                                updateURL({ status: e.target.value, page: 1 });
                            }}
                        >
                            {/* <option value=''>Select Status</option> */}
                            <option value='generated'>Generated</option>
                            <option value='completed'>Completed</option>
                            <option value='not-eligible'>Not Eligible</option>
                        </select>
                        <select
                            name='type'
                            id='type'
                            className='select'
                            value={selectedApproval}
                            onChange={(e) => {
                                updateURL({ approval: e.target.value, page: 1 });
                            }}
                        >
                            {/* <option value=''>Select Approval</option> */}
                            <option value='approved'>Approved</option>
                            <option value='not-approved'>Not Approved</option>
                        </select>
                    </div>
                </div>
                {billingData?.length === 0 && !loading ? (
                    <EmptyPage />
                ) : (
                    <div className='tableContainer'>
                        <table className='table'>
                            <thead className='tableHead'>
                                <tr>
                                    {tableHeader.map((head, headIndex) => {
                                        return <th key={headIndex}>{head}</th>;
                                    })}
                                </tr>
                            </thead>
                            <tbody>
                                {billingData?.map((row, rowIndex) => {
                                    return (
                                        <tr key={rowIndex} className='tableBodyRow'>
                                            <td>{row?.actId?.title ?? 'N/A'}</td>
                                            <td>{standardDate(row?.date?.split('T')[0]) ?? 'N/A'}</td>
                                            <td>{getBookedSlots(row) ?? '0'}</td>
                                            <td>
                                                {standardDate(row?.payoutDetails?.paymentDate?.split('T')[0]) ?? 'N/A'}
                                            </td>
                                            <td>₹ {row?.payoutDetails?.totalAmount ?? '0'}</td>
                                            <td>₹ {row?.payoutDetails?.commission ?? '0'}</td>
                                            <td>₹ {row?.payoutDetails?.amount ?? '0'}</td>
                                            <td>NA</td>
                                            {/* <td>
                                                <button className='actBtn payoutBtn noWidth'>Bill Generation</button>
                                            </td> */}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                        <div className='responsiveCardContainer'>
                            {billingData?.map((row, rowIndex) => (
                                <div key={rowIndex} className='responsiveCard'>
                                    <div className='cardContent'>
                                        <p>
                                            <strong>{tableHeader[0]}:</strong> {row?.contentId?.title ?? 'N/A'}
                                        </p>
                                        <p>
                                            <strong>{tableHeader[1]}:</strong>{' '}
                                            {standardDate(row?.date.split('T')[0]) ?? 'N/A'}
                                        </p>
                                        <p>
                                            <strong>{tableHeader[2]}:</strong> {getBookedSlots(row) ?? '0'}
                                        </p>
                                        <p>
                                            <strong>{tableHeader[3]}:</strong>{' '}
                                            {standardDate(row?.payoutDetails?.paymentDate?.split('T')[0]) ?? 'N/A'}
                                        </p>
                                        <p>
                                            <strong>{tableHeader[4]}:</strong> ₹{' '}
                                            {row?.payoutDetails?.totalAmount ?? '0'}
                                        </p>
                                        <p>
                                            <strong>{tableHeader[5]}:</strong> ₹ {row?.payoutDetails?.commission ?? '0'}
                                        </p>
                                        <p>
                                            <strong>{tableHeader[6]}:</strong> ₹ {row?.payoutDetails?.amount ?? '0'}
                                        </p>
                                        <div className='sendMessageBtn'>
                                            <button className='actBtn payoutBtn noWidth'>Bill Generation</button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}
                <Pagination
                    paginationClick={paginationClick}
                    numberOfRows={totalData}
                    offSet={offSet}
                    pageNumber={pageNumber}
                    limit={limit}
                />
            </div>
        </>
    );
};

export default ActivityBillings;
